import React, { useEffect, useState } from 'react';
import { Modal, Box, Typography, Button, IconButton, Input, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchResultGrid from '../searchResultGrid/searchResultGrid';
import modalStyle from '../../styles/modalStyle';
import { caseColumnDef } from '../../colDef/caseColumnDef';
import apiClient from '../../axiosClient';
import Case from '../../interfaces/case';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import DropDown from '../baseComponents/dropdown';
import { DATE, DROPDOWN, TEXT, updateableFields } from '../../constants/constants';
import Picklist from '../../interfaces/picklist';
import { createFilters } from '../../utils/filterUtils';
import Filter from '../../interfaces/filters';
import DropDownValue from '../baseComponents/dropdownvalue';
import { textPattern } from '../../constants/patterns';

interface CaseUpdateModalProps {
    open: boolean;
    onClose: () => void;
    selectedRows: Case[];
}

const CaseUpdateModal: React.FC<CaseUpdateModalProps> = ({ open, onClose, selectedRows }) => {
    const [fieldValue, setFieldValue] = useState('');
    const [fieldName, setFieldName] = useState('');
    const [filters, setFilters] = useState<Filter[]>([]);
    const segments = useSelector((state: RootState) => state.segments.segments);
    const clients = useSelector((state: RootState) => state.clients.clients);
    const agencies = useSelector((state: RootState) => state.agencies.agencies);
    const picklist = useSelector((state: RootState) => state.picklists.picklists);
    const districtAndStates = useSelector(
        (state: RootState) => state.districtsAndStates
    );
    const states = districtAndStates.states;
    const districts = districtAndStates.districts.map((val) => val.district);
    const userSlice = useSelector((state: RootState) => state.userList);
    const users = userSlice.users;
    const clientManagerList = userSlice.clientManagerDescendants;
    const setDefaultFilters = () => {
        const finalFilters = createFilters(
            updateableFields,
            segments,
            clients,
            agencies,
            picklist,
            states,
            districts,
            users,
            clientManagerList,
            false
        );
        setFilters(finalFilters);
    };
    const filterOptions = (options: any[], { inputValue }: { inputValue: string }, valueProp: string | undefined) => {
        const lower = inputValue.trim().toLowerCase();
        if (lower.length < 2) return [];         // require >= 2 chars
        const filtered = options.filter(opt => valueProp ? opt[valueProp].toLowerCase().includes(lower) : opt.toLowerCase().includes(lower));
        filtered.sort((a, b) => {
            const aStarts = valueProp ? a[valueProp].toLowerCase().startsWith(lower) : a.toLowerCase().startsWith(lower);
            const bStarts = valueProp ? b[valueProp].toLowerCase().startsWith(lower) : b.toLowerCase().startsWith(lower);
            if (aStarts && !bStarts) return -1;
            if (!aStarts && bStarts) return 1;
            return valueProp ? a[valueProp].localeCompare(b) : a.localeCompare(b);
        });
        return filtered;
    };
    useEffect(() =>{
        setDefaultFilters()
    },[])
    useEffect(()=>{
        setFieldValue('')
    },[fieldName])


    const handleSubmit = async () => {
        try {
            const case_query = selectedRows.map((row: Case) => {
                return {
                    client_reference_id: row.client_reference_id,
                    field_value: fieldValue
                }
            })
            const body = {
                field_name: fieldName,
                pool_month: selectedRows[0].pool_month,
                case_field_update: case_query
            }
            const response = await apiClient.post('api/v1/case/case_update', body);
            if (response.status === 200) {
                alert("Case Update successful");
            } else {
                alert("Case Update failed");
            }
        } catch (error: any) {
            if (error.response) {
                alert('Error response data:' + error.response.data.message);
            } else if (error.request) {
                alert('No response received:' + error.request);
            } else {
                alert('Error message:' + error.message);
            }
        }
        onClose();
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={modalStyle}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography id="modal-title" variant="h6">
                        Case Update
                    </Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <DropDown label="Select Field to update" items={updateableFields} selectedValue={fieldName} onSelection={(value) => setFieldName(value)} />
                {filters.map((filter, index) => {
                    if (filter.key === fieldName) {
                        if (filter.type === DROPDOWN) {
                            return (
                                <Box key={index} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, marginBottom: 2 }}>
                                    <Box width={'100%'} sx={{ flex: '5 1 0%', pointerEvents: filter.disabled ? 'none' : 'auto', opacity: filter.disabled ? 0.6 : 1 }}>
                                        <DropDownValue
                                            key={filter.key}
                                            label={filter.label}
                                            multiple={filter.multiple}
                                            filterOptions={filter.filterOptions ? filterOptions : undefined}
                                            selectedValue={fieldValue}
                                            onSelection={(value) => setFieldValue(value)}
                                            items={filter.list || []}
                                            valueProp={filter.valueProp}
                                            displayProp={filter.displayProp}
                                            keyProp={filter.keyProp}
                                            disabled={filter.disabled}
                                            noOptionsText={filter.noOptionsText}
                                        />
                                    </Box>
                                </Box>
                            );
                        } else if (filter.type === DATE) {
                            return (
                                <Box key={index} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, marginBottom: 2 }}>
                                    <Box width={'100%'} sx={{ flex: '5 1 0%', pointerEvents: filter.disabled ? 'none' : 'auto', opacity: filter.disabled ? 0.6 : 1 }}>
                                        <TextField
                                            type="date"
                                            onChange={e =>setFieldValue(e.target.value)}
                                            fullWidth
                                            value={fieldValue}
                                            label={filter.label}
                                            slotProps={{ inputLabel: { shrink: true } }}
                                        />
                                    </Box>
                                </Box>
                            );
                        } else if (filter.type === TEXT) {
                            return (
                                <Box key={index} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, marginBottom: 2 }}>
                                    <Box width={'100%'} sx={{ flex: '5 1 0%', pointerEvents: filter.disabled ? 'none' : 'auto', opacity: filter.disabled ? 0.6 : 1 }}>
                                        <TextField
                                            margin='none'
                                            label={filter.key === 'l0_interaction' ? 'L0_interaction' : filter.key}
                                            value={fieldValue}
                                            onChange={(e) => setFieldValue(e.target.value)}
                                            fullWidth
                                        />
                                    </Box>
                                </Box>
                            );
                        }
                    }
                }
                )
                }

                <Box mt={3}>
                    <Typography variant="subtitle1">Selected Rows:</Typography>
                    <SearchResultGrid
                        getRowId={(row) => row.case_id}
                        results={selectedRows}
                        onSelectRow={() => { }}
                        columns={caseColumnDef}
                        isRowSelectionAllowed={false}
                    />
                </Box>
                <Box mt={3} textAlign="right">
                    <Button variant="contained" color="primary" onClick={handleSubmit} disabled={fieldValue.trim() === ""} >Submit</Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default CaseUpdateModal;
